import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import {
  ArrowsClockwise,
  CircleNotch,
  TrendUp,
  TrendDown,
  MagnifyingGlass,
  Warning,
  Fire,
  FunnelSimple,
  ArrowSquareOut,
  Books,
} from "@phosphor-icons/react";
import { tradingViewStockUrl, screenerStockUrl } from "../lib/tradingview";

const REFRESH_MS = 60 * 1000; // auto-refresh every 60s

const fmtNum = (n) =>
  typeof n === "number" ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "—";
const fmtInt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : "—");

export default function TopGainers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [minPct, setMinPct] = useState(5);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("gainers"); // gainers | losers | both
  const [err, setErr] = useState("");
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);

  const load = useCallback(async (force = false) => {
    try {
      setErr("");
      const { data } = await api.get(`/top-movers?min_pct=${minPct}&limit=100${force ? "&force=true" : ""}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to fetch");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [minPct]);

  // Initial load + whenever minPct changes
  useEffect(() => {
    load(false);
  }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const t = setInterval(() => {
      setRefreshing(true);
      load(true);
      setCountdown(REFRESH_MS / 1000);
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Countdown ticker
  useEffect(() => {
    const id = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  const manualRefresh = async () => {
    setRefreshing(true);
    setCountdown(REFRESH_MS / 1000);
    await load(true);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    const filterFn = (r) => !q || r.symbol.includes(q) || (r.name || "").toUpperCase().includes(q);
    return {
      gainers: (data?.gainers || []).filter(filterFn),
      losers: (data?.losers || []).filter(filterFn),
    };
  }, [data, search]);

  if (loading) {
    return (
      <div className="p-8 border border-subtle bg-surface rounded-sm flex items-center gap-3" data-testid="gainers-loading">
        <CircleNotch className="animate-spin text-mod" size={18} weight="bold" />
        <div className="text-sm text-secondary-fg">Fetching NIFTY 500 gainers via Upstox…</div>
      </div>
    );
  }

  const fetched = data?.fetched_at
    ? new Date(data.fetched_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  return (
    <div data-testid="top-gainers">
      {/* Stale / error banner */}
      {(data?.stale || data?.error || err) && (
        <div
          className="mb-4 p-3 border border-mod-soft bg-mod-soft rounded-sm text-mod text-sm flex items-start gap-2"
          data-testid="gainers-stale-banner"
        >
          <Warning size={16} weight="bold" className="shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">
              {err ? "Could not refresh" : "Showing last snapshot"}
            </div>
            <div className="text-xs text-secondary-fg mt-0.5">
              {err || data?.error}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between mb-4 border-b border-subtle pb-3 gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg">
            /// NSE Universe · Top Movers
          </div>
          <div className="text-xs text-muted-fg mt-1">
            Gainers & losers with <span className="font-mono text-primary-fg">≥ {minPct}%</span> move · universe{" "}
            <span className="font-mono text-primary-fg">{data?.universe_size || 0}</span> · quoted{" "}
            <span className="font-mono text-primary-fg">{data?.quoted || 0}</span> · liquidity filtered · last {fetched}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            auto in <span className="text-pos tabular-nums">{countdown}s</span>
          </div>
          <button
            type="button"
            onClick={manualRefresh}
            disabled={refreshing}
            data-testid="gainers-refresh"
            className="px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest hover:bg-surface-hover text-secondary-fg hover:text-primary-fg transition flex items-center gap-2"
          >
            {refreshing ? (
              <CircleNotch size={12} weight="bold" className="animate-spin" />
            ) : (
              <ArrowsClockwise size={12} weight="bold" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-md px-3 py-2 border border-subtle rounded-sm bg-app">
          <MagnifyingGlass size={14} className="text-muted-fg" />
          <input
            data-testid="gainers-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search symbol or company…"
            className="w-full bg-transparent outline-none text-sm font-mono text-primary-fg placeholder:text-muted-fg"
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 border border-subtle rounded-sm bg-app">
          <FunnelSimple size={14} className="text-muted-fg" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">Min Move</span>
          {[2, 5, 10].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setMinPct(v)}
              data-testid={`gainers-threshold-${v}`}
              className={
                "px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest transition " +
                (minPct === v
                  ? "bg-pos-soft text-pos border border-pos-soft"
                  : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg border border-transparent")
              }
            >
              {v}%
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {[
            { k: "gainers", label: "Gainers", Icon: TrendUp },
            { k: "losers", label: "Losers", Icon: TrendDown },
            { k: "both", label: "Both", Icon: Fire },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              type="button"
              onClick={() => setView(k)}
              data-testid={`gainers-view-${k}`}
              className={
                "px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5 " +
                (view === k
                  ? k === "losers"
                    ? "bg-neg-soft border-neg-soft text-neg"
                    : "bg-pos-soft border-pos-soft text-pos"
                  : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg")
              }
            >
              <Icon size={12} weight="bold" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Gainers ≥ threshold" value={filtered.gainers.length} accent="text-pos" icon={<TrendUp size={12} weight="bold" />} />
        <Kpi label="Losers ≥ threshold" value={filtered.losers.length} accent="text-neg" icon={<TrendDown size={12} weight="bold" />} />
        <Kpi
          label="Biggest gain"
          value={filtered.gainers[0] ? `+${filtered.gainers[0].pct_change.toFixed(2)}%` : "—"}
          sub={filtered.gainers[0]?.symbol}
          accent="text-pos"
        />
        <Kpi
          label="Biggest drop"
          value={filtered.losers[0] ? `${filtered.losers[0].pct_change.toFixed(2)}%` : "—"}
          sub={filtered.losers[0]?.symbol}
          accent="text-neg"
        />
      </div>

      {/* Tables */}
      <div
        className={
          "grid gap-6 " +
          (view === "both" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")
        }
      >
        {(view === "gainers" || view === "both") && (
          <MoverTable
            title="Top Gainers"
            rows={filtered.gainers}
            positive
            testid="gainers-table"
          />
        )}
        {(view === "losers" || view === "both") && (
          <MoverTable
            title="Top Losers"
            rows={filtered.losers}
            positive={false}
            testid="losers-table"
          />
        )}
      </div>

      <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        data source: upstox market-quote · auto refresh 60s · universe nifty 500 · not investment advice
      </div>
    </div>
  );
}

function MoverTable({ title, rows, positive, testid }) {
  const tone = positive ? "text-pos" : "text-neg";
  return (
    <div className="border border-subtle rounded-sm overflow-hidden bg-surface" data-testid={testid}>
      <div className={"px-4 py-3 border-b border-subtle flex items-center justify-between " + (positive ? "bg-pos-soft" : "bg-neg-soft")}>
        <div className={"text-[10px] font-mono uppercase tracking-[0.25em] " + tone + " flex items-center gap-2"}>
          {positive ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
          {title}
        </div>
        <div className={"font-mono text-sm tabular-nums " + tone}>{rows.length}</div>
      </div>
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-subtle bg-app/40 text-muted-fg text-[10px] font-mono uppercase tracking-widest">
        <div className="col-span-3">Symbol</div>
        <div className="col-span-2 text-right">LTP</div>
        <div className="col-span-2 text-right">Change</div>
        <div className="col-span-1 text-right">% Δ</div>
        <div className="col-span-2 text-right">Volume</div>
        <div className="col-span-2 text-right pr-1">Open</div>
      </div>
      <div className="max-h-[65vh] overflow-y-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-fg">
            No symbols match the threshold right now.
          </div>
        ) : (
          rows.map((r, i) => (
            <motion.div
              key={r.symbol}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: Math.min(0.01 * i, 0.3) }}
              className="relative grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-subtle last:border-b-0 hover:bg-surface-hover transition"
              data-testid={`mover-row-${r.symbol}`}
            >
              <div
                className="absolute left-0 top-0 bottom-0 w-0.5"
                style={{ background: positive ? "hsl(160 84% 39%)" : "hsl(0 84% 60%)" }}
              />
              <div className="col-span-3 truncate">
                <div className="font-mono text-sm font-semibold text-primary-fg tracking-wide truncate">
                  {r.symbol}
                </div>
                {r.name && (
                  <div className="text-[10px] text-muted-fg truncate">{r.name}</div>
                )}
              </div>
              <div className="col-span-2 text-right font-mono text-sm tabular-nums text-primary-fg">
                ₹{fmtNum(r.ltp)}
              </div>
              <div className={"col-span-2 text-right font-mono text-sm tabular-nums " + tone}>
                {r.change >= 0 ? "+" : ""}
                {fmtNum(r.change)}
              </div>
              <div className={"col-span-1 text-right font-mono text-sm tabular-nums " + tone + " flex items-center justify-end gap-1"}>
                {positive ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
                {r.pct_change >= 0 ? "+" : ""}
                {r.pct_change.toFixed(2)}%
              </div>
              <div className="col-span-2 text-right font-mono text-xs tabular-nums text-secondary-fg">
                {fmtInt(r.volume)}
              </div>
              <div className="col-span-2 text-right pr-1">
                <div className="inline-flex items-center gap-1.5">
                  <a
                    href={tradingViewStockUrl(r.symbol)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`gainer-tv-${r.symbol}`}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:text-pos hover:border-pos/40 transition"
                    title={`Open ${r.symbol} chart on TradingView`}
                  >
                    <ArrowSquareOut size={10} weight="bold" />
                    TV
                  </a>
                  <a
                    href={screenerStockUrl(r.symbol)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`gainer-screener-${r.symbol}`}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:text-mod hover:border-mod/40 transition"
                    title={`Open ${r.symbol} fundamentals on Screener.in`}
                  >
                    <Books size={10} weight="bold" />
                    SCR
                  </a>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = "text-primary-fg", icon }) {
  return (
    <div className="p-3 border border-subtle rounded-sm bg-surface">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`font-mono text-xl tabular-nums ${accent}`}>{value}</div>
      {sub && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mt-0.5">
          {sub}
        </div>
      )}
    </div>
  );
}
